import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { lineString } from '@turf/helpers';
import length from '@turf/length';
import { bbox } from '@turf/turf';
import { RootState } from 'app/appStore';
import * as varColor from 'app/styles/colors';
import dayjs, { Dayjs } from 'dayjs';
import { DrawType } from 'entities/map';
import { GetActiveMachineryPerDayResponse } from 'entities/monitoring';
import { Position } from 'geojson';
import { LngLatBoundsLike, LngLatLike } from 'mapbox-gl';
import { getTime } from 'shared/lib';

import { IStatisticDataItem } from '../../../features/monitoring/ui/StatisticItemRow';

import {
  IFeature,
  IFeatureCollection,
  ITrackColor,
  ITracksInfo,
} from './types';

type IActiveMap = 'online' | 'archive';

type IMachinery = {
  machinery_id: number;
  machineryTitle: string;
};

type TrackColorArgs = { machinery_id: number; color: ITrackColor };

type IFarmlandMap = { [key: number]: { title: string; area: number } };
type IUnitsMap = { [key: number]: { model: string; number: string } };

type ICheckedMachineryIds = {
  [key: number]: { machineryTitle: string; isChecked: boolean };
};
type IActiveMachinery = GetActiveMachineryPerDayResponse['machinery'];

interface MonitoringState {
  allMachinery: IFeatureCollection | null;
  farmLands: IFeatureCollection | null;
  farmlandsMap: IFarmlandMap;
  unitsMap: IUnitsMap;
  workedPolygons: IFeatureCollection | null;
  tracksArchive: IFeatureCollection | null;
  tracksOnline: IFeatureCollection | null;
  filteredTracks: IFeatureCollection | null;
  lastMachineryPoint: IFeature | null;
  tracksInfo: ITracksInfo | null;
  tracksColors: { [key: number]: ITrackColor } | null;
  activeTracksOnline: { [key: number]: boolean } | null;
  drawType: DrawType;
  drawnArea: IFeatureCollection | null;
  drawnLine: IFeatureCollection | null;
  selectedMachinery: IMachinery | null;
  flyToCoordinates: LngLatLike | null;
  activeMap: IActiveMap;
  trackDate: Dayjs | null;
  machineryIdsArchiveTracks: ICheckedMachineryIds;
  machineryIdsOnlineTracks: { [key: number]: boolean };
  activeMachineryPerDay: IActiveMachinery | null;
  startTrackTime: Dayjs | null;
  endTrackTime: Dayjs | null;
  lastPointTime: Dayjs | null;
  isWorkedPolygonsOn: boolean;
  bounds: LngLatBoundsLike | null;
  statisticArchiveData: IStatisticDataItem[] | null;
}

const initialState: MonitoringState = {
  flyToCoordinates: null,
  filteredTracks: null,
  drawnArea: null,
  drawnLine: null,
  workedPolygons: null,
  selectedMachinery: null,
  lastMachineryPoint: null,
  drawType: DrawType.farmland_polygon,
  tracksArchive: null,
  tracksOnline: null,
  allMachinery: null,
  farmLands: null,
  farmlandsMap: {},
  unitsMap: {},
  tracksInfo: null,
  tracksColors: null,
  activeMap: 'online',
  trackDate: null,
  activeTracksOnline: null,
  machineryIdsArchiveTracks: {},
  machineryIdsOnlineTracks: {},
  activeMachineryPerDay: null,
  startTrackTime: dayjs().startOf('day'),
  endTrackTime: dayjs().endOf('day'),
  lastPointTime: null,
  isWorkedPolygonsOn: false,
  bounds: null,
  statisticArchiveData: null,
};

export const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    setTracksColors(
      state,
      action: PayloadAction<{ [key: number]: ITrackColor }>,
    ) {
      state.tracksColors = action.payload;
    },
    setActiveTracksOnline(
      state,
      action: PayloadAction<{ id: number; isActive: boolean }>,
    ) {
      if (!state.activeTracksOnline) {
        state.activeTracksOnline = {};
      }

      state.activeTracksOnline[action.payload.id] = action.payload.isActive;
    },
    removeActiveTrackOnline(state, action: PayloadAction<{ id: number }>) {
      const activeTracks = { ...state.activeTracksOnline };
      delete activeTracks[action.payload.id];
      state.activeTracksOnline = activeTracks;
    },
    removeTrackOnline(state, action: PayloadAction<{ id: number }>) {
      const trackFeatures: IFeature[] =
        state.tracksOnline?.features.filter(
          (feature) => feature.properties?.machineryId !== action.payload.id,
        ) || [];

      state.tracksOnline = {
        type: 'FeatureCollection',
        features: trackFeatures,
      };
    },
    setTracksOnline(state, action: PayloadAction<IFeature[]>) {
      if (state.tracksOnline) {
        state.tracksOnline.features.push(...action.payload);
      } else {
        state.tracksOnline = {
          type: 'FeatureCollection',
          features: action.payload,
        };
      }
    },
    setStatisticArchiveData(
      state,
      action: PayloadAction<IStatisticDataItem[]>,
    ) {
      state.statisticArchiveData = action.payload;
    },
    resetStatisticArchiveData(state) {
      state.statisticArchiveData = initialState.statisticArchiveData;
    },
    setUnitsMap(state, action: PayloadAction<IUnitsMap>) {
      state.unitsMap = action.payload;
    },
    setBounds(state, action: PayloadAction<LngLatBoundsLike>) {
      state.bounds = action.payload;
    },
    setIsWorkedPolygonsOn(state, action: PayloadAction<boolean>) {
      state.isWorkedPolygonsOn = action.payload;
    },
    resetIsWorkedPolygonsOn(state) {
      state.isWorkedPolygonsOn = initialState.isWorkedPolygonsOn;
    },
    setWorkedPolygons(state, action: PayloadAction<IFeatureCollection>) {
      state.workedPolygons = action.payload;
    },
    resetWorkedPolygons(state) {
      state.workedPolygons = initialState.workedPolygons;
    },
    setStartTrackTime(state, action: PayloadAction<Dayjs>) {
      state.startTrackTime = action.payload;
      monitoringSlice.caseReducers.updateFilteredGroupTracks(state);
    },
    setEndTrackTime(state, action: PayloadAction<Dayjs>) {
      state.endTrackTime = action.payload;
      monitoringSlice.caseReducers.updateFilteredGroupTracks(state);
    },
    setTracksArchive(state, action: PayloadAction<IFeature[]>) {
      if (state.tracksArchive) {
        state.tracksArchive.features.push(...action.payload);
      } else {
        state.tracksArchive = {
          type: 'FeatureCollection',
          features: action.payload,
        };
      }
      const lastMachineryPoint = action.payload.slice(-2)[0];
      state.lastMachineryPoint = lastMachineryPoint;
      state.lastPointTime = dayjs(lastMachineryPoint.properties?.fullTime);
      monitoringSlice.caseReducers.updateFilteredGroupTracks(state);
    },
    updateFilteredGroupTracks(state) {
      if (!state.tracksArchive || !state.startTrackTime || !state.endTrackTime)
        return;

      const startTime = getTime(state.startTrackTime);
      const endTime = getTime(state.endTrackTime);

      const linesCoordinates: Record<string, Position[]> = {};

      const filteredTracks: IFeatureCollection = {
        ...state.tracksArchive,
        features: state.tracksArchive.features.filter((feature) => {
          const featureTime = feature.properties?.pointTime;
          return featureTime >= startTime && featureTime <= endTime;
        }),
      };

      filteredTracks.features.forEach((feature) => {
        if (feature.geometry.type === 'Point') {
          const trackPoint = feature.geometry as {
            type: 'Point';
            coordinates: [number, number];
          };

          const machineryId = feature.properties?.machineryId;
          if (!machineryId) return;

          if (!linesCoordinates[machineryId]) {
            linesCoordinates[machineryId] = [];
          }
          linesCoordinates[machineryId].push(trackPoint.coordinates);
        }
      });

      Object.entries(linesCoordinates).forEach(
        ([machineryId, lineCoordinates]) => {
          if (lineCoordinates.length > 1) {
            const trackColor =
              state.tracksInfo?.[+machineryId]?.color.primary ||
              varColor.orange;

            const lineFeature: IFeature = {
              type: 'Feature',
              id: machineryId,
              properties: {
                machineryId,
                color: trackColor,
              },
              geometry: {
                type: 'LineString',
                coordinates: lineCoordinates,
              },
            };
            filteredTracks.features.push(lineFeature);

            const distanceTrack = length(lineString(lineCoordinates), {
              units: 'kilometers',
            }).toFixed(3);

            state.tracksInfo = {
              ...state.tracksInfo,
              [machineryId]: {
                ...state.tracksInfo?.[+machineryId],
                distance: distanceTrack,
              },
            };
          }
        },
      );

      state.filteredTracks = {
        type: 'FeatureCollection',
        features: filteredTracks.features,
      };
    },
    setLastPointTime(state, action: PayloadAction<Dayjs>) {
      state.lastPointTime = action.payload;
    },
    setDrawnLine: (state, action: PayloadAction<IFeatureCollection>) => {
      state.drawnLine = action.payload;
    },
    setDrawnArea: (state, action: PayloadAction<IFeatureCollection>) => {
      state.drawnArea = action.payload;
    },

    setFarmLands(state, action: PayloadAction<IFeatureCollection>) {
      state.farmLands = action.payload;
      state.bounds = bbox(action.payload) as LngLatBoundsLike;
    },
    setSelectedMachinery(state, action: PayloadAction<IMachinery>) {
      state.selectedMachinery = action.payload;
    },

    setAllMachinery(state, action: PayloadAction<IFeatureCollection>) {
      state.allMachinery = action.payload;
    },
    setTrackInfo(state, action: PayloadAction<TrackColorArgs>) {
      const { machinery_id, color } = action.payload;

      if (!state.tracksInfo) {
        state.tracksInfo = {};
      }
      if (!state.tracksInfo[machinery_id]) {
        state.tracksInfo[machinery_id] = { color, distance: 0 };
      } else {
        state.tracksInfo[machinery_id] = {
          ...state.tracksInfo[machinery_id],
          color,
        };
      }
    },

    setActiveMap(state, action: PayloadAction<IActiveMap>) {
      state.activeMap = action.payload;
    },
    setTrackDate(state, action: PayloadAction<Dayjs | null>) {
      state.trackDate = action.payload;
    },
    resetSelectedMachinery(state) {
      state.selectedMachinery = null;
    },
    setLastMachineryPoint(state, action: PayloadAction<IFeature>) {
      state.lastMachineryPoint = action.payload;
    },
    setMachineryIdsArchiveTracks(
      state,
      action: PayloadAction<ICheckedMachineryIds>,
    ) {
      state.machineryIdsArchiveTracks = action.payload;
    },
    setFarmlandsMap(state, action: PayloadAction<IFarmlandMap>) {
      state.farmlandsMap = action.payload;
    },
    setActiveMachineryPerDay(state, action: PayloadAction<IActiveMachinery>) {
      state.activeMachineryPerDay = action.payload;
    },

    setDrawType(state, action: PayloadAction<DrawType>) {
      state.drawType = action.payload;
    },
    setFlyToCoordinates(state) {
      const geometry = state.lastMachineryPoint?.geometry;
      if (geometry && 'coordinates' in geometry) {
        state.flyToCoordinates = geometry.coordinates as LngLatLike;
      }
    },
    resetDrawnLine: (state) => {
      state.drawnLine = initialState.drawnLine;
    },
    resetTrackDate: (state) => {
      state.trackDate = initialState.trackDate;
    },
    resetMapData: (state) => {
      state.drawType = initialState.drawType;
      state.drawnLine = initialState.drawnLine;
      state.drawnArea = initialState.drawnArea;
    },
    resetTracksArchive(state) {
      state.tracksArchive = initialState.tracksArchive;
      state.filteredTracks = initialState.filteredTracks;
      state.workedPolygons = initialState.workedPolygons;
      state.isWorkedPolygonsOn = initialState.isWorkedPolygonsOn;
    },
    resetDrawnArea: (state) => {
      state.drawnArea = initialState.drawnArea;
    },
    resetLastMachineryPoint(state) {
      state.lastMachineryPoint = initialState.lastMachineryPoint;
    },
    resetTracksInfo(state) {
      state.tracksInfo = initialState.tracksInfo;
    },

    resetMachineryIdsArchiveTracks(state) {
      state.machineryIdsArchiveTracks = {};
    },
    resetActiveMachineryPerDay(state) {
      state.activeMachineryPerDay = initialState.activeMachineryPerDay;
    },
    resetStartEndTrackTime(state) {
      state.startTrackTime = initialState.startTrackTime;
      state.endTrackTime = initialState.endTrackTime;
    },
    resetStateGroupMode(state) {
      state.tracksArchive = initialState.tracksArchive;
      state.trackDate = initialState.trackDate;
      state.tracksInfo = initialState.tracksInfo;
      state.selectedMachinery = initialState.selectedMachinery;
      state.machineryIdsArchiveTracks = initialState.machineryIdsArchiveTracks;
      state.activeMachineryPerDay = initialState.activeMachineryPerDay;
    },
  },
});

export const selectMonitoringSlice = (state: RootState) => state.monitoring;

export const {
  setBounds,
  setDrawnLine,
  resetDrawnLine,
  setDrawnArea,
  resetDrawnArea,
  resetWorkedPolygons,
  setIsWorkedPolygonsOn,
  resetIsWorkedPolygonsOn,
  setFarmLands,
  setTrackDate,
  setActiveMap,
  setAllMachinery,
  setTrackInfo,
  setWorkedPolygons,
  setMachineryIdsArchiveTracks,
  setTracksArchive,
  setStartTrackTime,
  setEndTrackTime,
  resetMapData,
  setDrawType,
  setSelectedMachinery,
  setActiveMachineryPerDay,
  setLastMachineryPoint,
  setFlyToCoordinates,
  setLastPointTime,
  resetTracksArchive,
  resetTracksInfo,
  resetSelectedMachinery,
  resetTrackDate,
  resetActiveMachineryPerDay,
  resetMachineryIdsArchiveTracks,
  resetStartEndTrackTime,
  resetStateGroupMode,
  resetLastMachineryPoint,
  setFarmlandsMap,
  setUnitsMap,
  setStatisticArchiveData,
  resetStatisticArchiveData,
  setTracksOnline,
  setActiveTracksOnline,
  removeActiveTrackOnline,
  removeTrackOnline,
  setTracksColors,
} = monitoringSlice.actions;
